import { m4, m4LookAt, m4Ortho, m4Perspective, type Mat4 } from '@/math/mat4';
import { v3, type Vec3 } from '@/math/vec3';

export interface Camera {
  readonly view: Mat4;
  readonly proj: Mat4;
  resize(width: number, height: number): void;
  update(dtSec: number): void;
}

const ELEVATION_LIMIT = Math.PI / 2 - 0.05;

const computeEye = (
  out: Vec3,
  target: Vec3,
  azimuth: number,
  elevation: number,
  distance: number,
): void => {
  const c = Math.cos(elevation);
  const s = Math.sin(elevation);
  out[0] = target[0]! + distance * c * Math.cos(azimuth);
  out[1] = target[1]! + distance * s;
  out[2] = target[2]! + distance * c * Math.sin(azimuth);
};

export class IsoCamera implements Camera {
  readonly view = m4();
  readonly proj = m4();
  readonly target = v3(0, 0, 0);
  private readonly eye = v3();
  private readonly up = v3(0, 1, 0);

  azimuth = Math.PI / 4;
  elevation = Math.atan(1 / Math.SQRT2); // ~35.264°, true ¾ iso
  distance = 20;
  viewHalfHeight = 6;
  near = 0.1;
  far = 200;

  resize(width: number, height: number): void {
    const aspect = width / height;
    const h = this.viewHalfHeight;
    m4Ortho(this.proj, -h * aspect, h * aspect, -h, h, this.near, this.far);
  }

  update(_dtSec: number): void {
    computeEye(this.eye, this.target, this.azimuth, this.elevation, this.distance);
    m4LookAt(this.view, this.eye, this.target, this.up);
  }
}

export class OrbitCamera implements Camera {
  readonly view = m4();
  readonly proj = m4();
  readonly target = v3(0, 0, 0);
  private readonly eye = v3();
  private readonly up = v3(0, 1, 0);

  azimuth = Math.PI / 4;
  elevation = Math.PI / 6;
  distance = 8;
  fovY = Math.PI / 3;
  near = 0.1;
  far = 200;

  resize(width: number, height: number): void {
    m4Perspective(this.proj, this.fovY, width / height, this.near, this.far);
  }

  update(_dtSec: number): void {
    computeEye(this.eye, this.target, this.azimuth, this.elevation, this.distance);
    m4LookAt(this.view, this.eye, this.target, this.up);
  }

  attachInput(target: HTMLElement): () => void {
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (e: PointerEvent): void => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      target.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent): void => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      this.azimuth -= dx * 0.005;
      this.elevation += dy * 0.005;
      if (this.elevation > ELEVATION_LIMIT) this.elevation = ELEVATION_LIMIT;
      if (this.elevation < -ELEVATION_LIMIT) this.elevation = -ELEVATION_LIMIT;
    };
    const onUp = (): void => {
      dragging = false;
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const factor = 1 + Math.sign(e.deltaY) * 0.1;
      this.distance = Math.max(1, Math.min(80, this.distance * factor));
    };
    target.addEventListener('pointerdown', onDown);
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
    target.addEventListener('wheel', onWheel, { passive: false });
    return (): void => {
      target.removeEventListener('pointerdown', onDown);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      target.removeEventListener('wheel', onWheel);
    };
  }
}
